import { Component } from '@angular/core';
import { ITooltipParams } from 'ag-grid-community';
import { AppscriptsNumber } from '../../models/appscripts-user.model';
import { CustomUtils } from '../../services/custom-utils';
import { SchedulerService } from '../../services/scheduler.service';

@Component({
  standalone: false,
  selector: 'app-ag-tooltip',
  templateUrl: './ag-tooltip.component.html',
  styleUrls: ['./ag-tooltip.component.scss'],
})
export class AgTooltipComponent {
  private params!: ITooltipParams;
  private currentHoverFieldId: string;
  tooltipData = '';

  agInit(params: ITooltipParams): void {
    this.params = params;
    if (params.location === 'header') {
      this.tooltipData = params.value;
      return;
    }
    const currentRow = params.data;
    this.currentHoverFieldId = params.column?.getUniqueId();
    switch (this.currentHoverFieldId) {
      case 'senderDelivered':
        if (currentRow.senderDelivered !== currentRow.senderSent) {
          this.tooltipData = currentRow.senderSent;
        }
        break;
      case 'callerId':
        if (currentRow.callerId !== currentRow.sipCallerId) {
          this.tooltipData = currentRow.sipCallerId;
        }
        break;
      case 'user':
        this.tooltipData = currentRow.user.username;
        break;
      case 'supplierCount':
        this.tooltipData = currentRow.supplierCount ? 'Show all' : null;
        break;
      case 'testDto':
        this.tooltipData = 'See test details in Live number testing';
        break;
      case 'scheduledTaskTitle':
        this.tooltipData = 'View scheduled task settings';
        break;
      case 'reportRuleDto':
        this.tooltipData = SchedulerService.handleReportRuleDto(currentRow);
        break;
      case 'phoneNumber':
        this.tooltipData = currentRow.blocked ? 'The phone number is blocked' : null;
        break;
      case 'mtSmsCount': {
        const mtDate = currentRow.lastMtSmsAt ? CustomUtils.formatDate(currentRow.lastMtSmsAt).join(' ') : null;
        const mtStatus = this.getMTStatus(currentRow);
        this.tooltipData = mtDate ? `${mtStatus} <br> Last successful MT test: ${mtDate}` : mtStatus;
        break;
      }
      case 'moSmsCount': {
        const moDate = currentRow.lastMoSmsAt ? CustomUtils.formatDate(currentRow.lastMoSmsAt).join(' ') : null;
        const moStatus = this.getMOStatus(currentRow);
        this.tooltipData = moDate ? `${moStatus} <br> Last successful MO test: ${moDate}` : moStatus;
        break;
      }
      case 'voiceCallCount': {
        const voiceDate = currentRow.lastVoiceCallAt ? CustomUtils.formatDate(currentRow.lastVoiceCallAt).join(' ') : null;
        const voiceStatus = this.getCLIStatus(currentRow);
        this.tooltipData = voiceDate ? `${voiceStatus} <br> Last successful CLI test: ${voiceDate}` : voiceStatus;
        break;
      }
      case 'healthCheck':
        this.tooltipData = this.getToolTipDataForHealthCheck(currentRow);
        break;
      default:
        this.tooltipData = null;
    }
  }

  getToolTipDataForHealthCheck(currentRow): string {
    const isDeviceTokenValid = currentRow.deviceToken?.length > 0;
    const isSimStateValid = currentRow.simState === 'SIM_STATE_READY';
    const isServiceStateValid = currentRow.serviceState === 'IN_SERVICE' || currentRow.serviceState === 'STATE_IN_SERVICE';
    const isSimIdValid = currentRow.simId?.length > 0;
    const isRoaming = currentRow.roaming;
    const hasSignal = currentRow.lastSignalStrength && currentRow.lastSignalStrength >= 1;
    const isPendingSimIdCheck = currentRow.waitingSimIdCheck;

    const issues: string[] = [];
    if (!isDeviceTokenValid) {
      issues.push('Device token is missing');
    }
    if (!isSimStateValid) {
      issues.push('SIM state is not ready');
    }
    if (!isServiceStateValid) {
      issues.push('Service state is not in service');
    }
    if (!isSimIdValid) {
      issues.push('SIM ID is missing');
    }
    if (!hasSignal) {
      issues.push('No signal');
    }
    if (isRoaming) {
      issues.push('Roaming');
    }
    if (isPendingSimIdCheck) {
      issues.push('Pending SIM ID check');
    }
    return issues.length > 0 ? issues.join(', ') : null;
  }

  private getMTStatus(row: AppscriptsNumber): string {
    const data = { ...row, mtOptedIn: true }; // Because mtOptedIn is always true ...
    if (!data.mtAllowed) return 'MT is not allowed';
    if (data.mtAllowed && !data.mtOptedIn) return 'MT is allowed but not opted in';
    if (data.mtAllowed && data.mtOptedIn) {
      if (data.mtEnabled) return 'MT is enabled';
      return 'MT is opted in but not enabled';
    }
  }

  private getMOStatus(data: AppscriptsNumber): string {
    if (!data.moAllowed) return 'MO is not allowed';
    if (data.moAllowed && !data.moOptedIn) return 'MO is allowed but not opted in';
    if (data.moAllowed && data.moOptedIn) {
      if (data.moEnabled) return 'MO is enabled';
      return 'MO is opted in but not enabled';
    }
  }

  private getCLIStatus(data: AppscriptsNumber): string {
    if (!data.cliAllowed) return 'CLI is not allowed';
    if (data.cliAllowed && !data.cliOptedIn) return 'CLI is allowed but not opted in';
    if (data.cliAllowed && data.cliOptedIn) {
      if (data.cliEnabled) return 'CLI is enabled';
      return 'CLI is opted in but not enabled';
    }
  }
}
